import React from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowRight, Edit2, ShoppingBag, Trash2 } from "lucide-react";
import { imgUri, useMenu, ORDERING_ENABLED } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { lineTotal, useCart } from "../lib/cart";
import { chf } from "../lib/format";
import { Header } from "../components/Header";
import { Stepper } from "../components/Stepper";

export default function CartPage() {
  const navigate = useNavigate();
  const { t, tx } = useI18n();
  const cart = useCart();
  const { data } = useMenu();
  const settings = data?.settings;
  const goods = cart.subtotal + cart.extrasTotal;

  return (
    <div className="min-h-screen bg-hm-surface pb-32">
      <Header />
      <div className="mx-auto w-full max-w-[760px] px-4">
        <div className="flex items-end justify-between border-b border-hm-line py-4">
          <h1 className="font-display text-3xl text-hm-ink" data-testid="cart-title">{t("cart")}</h1>
          {cart.items.length > 0 ? <span className="font-sans text-sm text-hm-muted">{cart.count} {t("items").toLowerCase()}</span> : null}
        </div>

        {cart.items.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-24 text-center">
            <ShoppingBag size={40} className="text-hm-muted" />
            <p className="font-display text-2xl text-hm-ink">{t("emptyCart")}</p>
            <p className="max-w-xs font-sans text-[15px] text-hm-muted">{t("emptyCartHint")}</p>
            <button
              type="button"
              data-testid="cart-browse-menu-button"
              onClick={() => navigate("/")}
              className="mt-2 rounded-full bg-hm-brand px-6 py-3 font-sans font-extrabold text-white"
            >
              {t("browseMenu")}
            </button>
          </div>
        ) : (
          <>
            <div className="pt-2">
              {cart.items.map((it) => (
                <div key={it.line_id} className="flex gap-3.5 border-b border-hm-line py-3.5" data-testid={`cart-item-${it.line_id}`}>
                  <img src={imgUri(it.image_url)} alt={tx(it.name)} className="h-[92px] w-[92px] flex-shrink-0 rounded-2xl bg-hm-tertiary object-cover" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <span className="line-clamp-1 flex-1 font-display text-lg text-hm-ink">{tx(it.name)}{it.size ? ` · ${it.size.label}` : ""}</span>
                      <span className="font-sans text-base font-extrabold text-hm-ink">{chf(lineTotal(it))}</span>
                    </div>
                    {it.options.map((o) => (
                      <p key={o.key} className="mt-0.5 font-sans text-[13px] text-hm-olive">• {tx(o.name)}{o.price > 0 ? ` (+${chf(o.price)})` : ""}</p>
                    ))}
                    {it.removed_ingredients.map((r) => (
                      <p key={r.id} className="mt-0.5 font-sans text-[13px] text-hm-error">− {t("without")} {tx(r)}</p>
                    ))}
                    {it.extras.map((e) => (
                      <p key={e.extra_id} className="mt-0.5 font-sans text-[13px] text-hm-olive">+ {e.quantity > 1 ? `${e.quantity}x ` : ""}{tx(e.name)} ({chf(e.unit_price * e.quantity)})</p>
                    ))}
                    {it.note ? <p className="mt-0.5 font-sans text-[13px] italic text-hm-muted">« {it.note} »</p> : null}
                    <div className="mt-2.5 flex items-center justify-between">
                      <Stepper value={it.quantity} min={1} size={32} onChange={(v) => cart.updateItem(it.line_id, { quantity: v })} testId={`cart-qty-${it.line_id}`} />
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          data-testid={`cart-edit-${it.line_id}`}
                          onClick={() => navigate(`/product/${it.product_id}?line=${it.line_id}`)}
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-hm-tertiary text-hm-ink"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          type="button"
                          data-testid={`cart-remove-${it.line_id}`}
                          onClick={() => cart.removeItem(it.line_id)}
                          className="flex h-9 w-9 items-center justify-center rounded-full bg-hm-tertiary text-hm-error"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              data-testid="cart-add-more-button"
              onClick={() => navigate("/")}
              className="mt-3 inline-flex items-center gap-2 font-sans text-sm font-bold text-hm-brand transition-opacity hover:opacity-80"
            >
              <ArrowLeft size={16} /> {t("addMoreProducts")}
            </button>

            <div className="mt-4">
              <label className="mb-2 block font-sans text-[13px] font-semibold uppercase tracking-wide text-hm-muted">{t("orderNote")}</label>
              <textarea
                data-testid="cart-general-note-input"
                value={cart.generalNote}
                onChange={(e) => cart.setGeneralNote(e.target.value)}
                placeholder={t("orderNotePlaceholder")}
                rows={3}
                className="w-full rounded-xl border border-hm-line bg-hm-card px-3.5 py-3 font-sans text-base text-hm-ink outline-none focus:border-hm-brand"
              />
            </div>

            <div className="mt-6 rounded-3xl bg-hm-inverse p-5 text-white">
              <Row label={t("subtotal")} value={chf(cart.subtotal)} />
              <Row label={t("extrasTotal")} value={chf(cart.extrasTotal)} />
              {settings?.delivery_enabled ? (
                <Row
                  label={`${t("deliveryFee")} (${t("delivery").toLowerCase()})`}
                  value={settings.free_delivery_from && goods >= settings.free_delivery_from ? t("free") : chf(settings.delivery_fee)}
                  muted
                />
              ) : null}
              <div className="my-2 h-px bg-white/15" />
              <div className="flex justify-between">
                <span className="font-display text-2xl">{t("total")}</span>
                <span className="font-display text-2xl" data-testid="cart-total">{chf(goods)}</span>
              </div>
              <p className="mt-2 font-sans text-xs text-white/70">{t("noOnlinePayment")}</p>
            </div>
          </>
        )}
      </div>

      {cart.items.length > 0 ? (
        <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-hm-line bg-hm-surface px-4 pt-3 pb-4">
          <div className="mx-auto w-full max-w-[760px]">
            {!ORDERING_ENABLED ? (
              <div className="mb-3 flex items-start gap-3 rounded-2xl border border-hm-error/30 bg-hm-error/10 p-3" data-testid="cart-ordering-unavailable">
                <AlertTriangle size={18} className="mt-0.5 flex-shrink-0 text-hm-error" />
                <p className="font-sans text-[13px] text-hm-ink">{t("orderingUnavailableBody")}</p>
              </div>
            ) : null}
            <button
              type="button"
              data-testid="cart-checkout-button"
              onClick={() => navigate("/checkout")}
              disabled={!ORDERING_ENABLED}
              className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-hm-brand font-sans text-base font-extrabold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
            >
              {!ORDERING_ENABLED ? (
                <><AlertTriangle size={18} /> {t("orderingUnavailableTitle")}</>
              ) : (
                <>{t("checkout")} · {chf(goods)} <ArrowRight size={18} /></>
              )}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, value, muted }) {
  return (
    <div className={`flex justify-between ${muted ? "opacity-70" : ""}`}>
      <span className="font-sans text-sm text-white/80">{label}</span>
      <span className="font-sans text-sm font-semibold text-white">{value}</span>
    </div>
  );
}
